/* global crypto */
/**
 * AI Skills Assessment Service
 * 
 * Manages conversational AI-powered skill assessments for Virtual Assistants.
 * Features:
 * - Create and manage assessment sessions
 * - Submit and evaluate user responses
 * - Calculate final scores and proficiency levels
 * - Update user profiles with verified skills
 * - Track assessment history and cooldowns
 * 
 * Environment Variables:
 * - VITE_AI_ASSESSMENT_MAX_ROUNDS: Maximum questions per assessment (default: 4)
 * - VITE_AI_ASSESSMENT_PASS_THRESHOLD: Score needed to pass (default: 70)
 * - VITE_AI_ASSESSMENT_RETRY_COOLDOWN_HOURS: Hours between retakes (default: 1)
 */

import supabase from '../utils/supabase';
import { SupabaseError } from '../utils/errorHandling';
import { 
  generateAssessmentQuestion, 
  evaluateAssessmentResponse, 
  generateAssessmentFeedback 
} from '../utils/aiService';

// Configuration
const MAX_ROUNDS = parseInt(import.meta.env.VITE_AI_ASSESSMENT_MAX_ROUNDS) || 4;
const PASS_THRESHOLD = parseInt(import.meta.env.VITE_AI_ASSESSMENT_PASS_THRESHOLD) || 70;
const RETRY_COOLDOWN_HOURS = parseInt(import.meta.env.VITE_AI_ASSESSMENT_RETRY_COOLDOWN_HOURS) || 1;

class AISkillsAssessmentService {
  /**
   * Start a new AI skills assessment session
   * @param {string} userId - User ID
   * @param {string} skillName - Name of the skill to assess
   * @returns {Promise<Object>} Session info with first question
   */
  async startAssessment(userId, skillName) {
    try {
      // 1. Check if user can retake (cooldown check)
      const canRetake = await this.checkRetakeCooldown(userId, skillName);
      if (!canRetake.allowed) {
        throw new Error(canRetake.message);
      }

      // 2. Try to map skill name to skill ID (optional)
      const skillId = await this.getSkillIdByName(skillName);

      // 3. Generate first question
      console.log('[AISkillsAssessmentService] Generating first question for:', skillName);
      const questionData = await generateAssessmentQuestion(skillName, [], 'intermediate');
      console.log('[AISkillsAssessmentService] Question generated:', questionData);

      // Validate question data
      if (!questionData || !questionData.question) {
        console.error('[AISkillsAssessmentService] Invalid question data:', questionData);
        throw new Error('Failed to generate valid question. Please try again.');
      }

      // 4. Create conversation log entry with first question
      const initialConversationLog = [{
        type: 'ai',
        content: questionData.question,
        round: 1,
        difficulty: questionData.difficulty,
        expectedTopics: questionData.expectedTopics || []
      }];

      // 5. Create session record
      const { data: session, error } = await supabase
        .from('ai_skill_assessment_sessions')
        .insert({
          user_id: userId,
          skill_name: skillName,
          skill_id: skillId,
          conversation_log: initialConversationLog,
          started_at: new Date().toISOString(),
          rounds_completed: 0,
          ai_model_used: import.meta.env.VITE_AI_MODEL || 'gemini-2.5-flash'
        })
        .select()
        .single();

      if (error) {
        throw new SupabaseError(error.message, error, { table: 'ai_skill_assessment_sessions', action: 'insert' });
      }

      return {
        sessionId: session.id,
        question: questionData.question,
        difficulty: questionData.difficulty,
        progress: 0.25, // 25% - first question out of 4
        roundNumber: 1,
        expectedTopics: questionData.expectedTopics || []
      };
    } catch (error) {
      console.error('[AISkillsAssessmentService] startAssessment error:', error);
      throw error;
    }
  }

  /**
   * Submit user response and get next question or finalize
   * @param {string} sessionId - Assessment session ID
   * @param {string} userAnswer - User's answer to current question
   * @returns {Promise<Object>} Next question or final results
   */
  async submitResponse(sessionId, userAnswer) {
    try {
      // 1. Fetch session record
      const { data: session, error: fetchError } = await supabase
        .from('ai_skill_assessment_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (fetchError) {
        throw new SupabaseError(fetchError.message, fetchError, { table: 'ai_skill_assessment_sessions', action: 'fetch' });
      }

      if (!session) {
        throw new Error('Assessment session not found');
      }

      // 2. Get current conversation log and determine round
      const conversationLog = session.conversation_log || [];
      
      // Count how many complete Q&A exchanges we have
      // Each round = 1 AI question + 1 user answer
      const completedRounds = conversationLog.filter(entry => entry.user_answer).length;
      const currentRound = completedRounds + 1;
      
      console.log(`[AISkillsAssessmentService] Processing round ${currentRound} of ${MAX_ROUNDS}`);

      // Get the current AI question (most recent entry without user_answer)
      const currentQuestionEntry = conversationLog.find(entry => 
        entry.type === 'ai' && !entry.user_answer
      ) || conversationLog[conversationLog.length - 1];
      
      const currentQuestion = currentQuestionEntry?.content || currentQuestionEntry?.question || 'Assessment question';

      // 3. Truncate very long answers for evaluation (keep first 1000 chars for scoring)
      const truncatedAnswer = userAnswer.length > 1000 
        ? userAnswer.substring(0, 1000) + '...' 
        : userAnswer;

      // 4. Evaluate response using AI
      const evaluation = await evaluateAssessmentResponse(
        currentQuestion,
        truncatedAnswer,
        session.skill_name
      );

      // 5. Update the current question entry with user's answer and evaluation
      const updatedLog = conversationLog.map((entry, idx) => {
        // Find the matching AI question entry and add user response
        if (idx === conversationLog.length - 1 && entry.type === 'ai' && !entry.user_answer) {
          return {
            ...entry,
            user_answer: userAnswer, // Store full answer in DB
            ai_evaluation: {
              clarity: evaluation.clarity,
              relevance: evaluation.relevance,
              depth: evaluation.depth,
              technical_accuracy: evaluation.technical_accuracy,
              score: evaluation.score,
              comments: evaluation.comments || ''
            }
          };
        }
        return entry;
      });

      // 6. Update rounds completed count
      const roundsCompleted = currentRound;

      console.log(`[AISkillsAssessmentService] Rounds completed: ${roundsCompleted}/${MAX_ROUNDS}`);

      // 7. Check if we should finalize (ONLY after completing all 4 rounds)
      // Do NOT use followUpNeeded to terminate early
      const shouldFinalize = roundsCompleted >= MAX_ROUNDS;

      if (shouldFinalize) {
        console.log('[AISkillsAssessmentService] All rounds complete, finalizing assessment...');
        const result = await this.finalizeAssessment(sessionId, updatedLog);
        return {
          completed: true,
          result
        };
      }

      // 8. Generate next question for the next round
      const nextRound = roundsCompleted + 1;
      console.log(`[AISkillsAssessmentService] Generating question for round ${nextRound}...`);

      const nextQuestionData = await generateAssessmentQuestion(
        session.skill_name,
        updatedLog,
        evaluation.suggestedDifficulty || 'intermediate'
      );

      // 9. Add new question to conversation log
      updatedLog.push({
        type: 'ai',
        content: nextQuestionData.question,
        round: nextRound,
        difficulty: nextQuestionData.difficulty,
        expectedTopics: nextQuestionData.expectedTopics || []
      });

      // 10. Update session with new conversation log
      const { error: updateError } = await supabase
        .from('ai_skill_assessment_sessions')
        .update({
          conversation_log: updatedLog,
          rounds_completed: roundsCompleted,
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (updateError) {
        throw new SupabaseError(updateError.message, updateError, { table: 'ai_skill_assessment_sessions', action: 'update' });
      }

      return {
        completed: false,
        nextQuestion: nextQuestionData.question,
        difficulty: nextQuestionData.difficulty,
        progress: nextRound / MAX_ROUNDS,
        roundNumber: nextRound,
        expectedTopics: nextQuestionData.expectedTopics || [],
        previousScore: evaluation.score,
        previousFeedback: evaluation.comments
      };
    } catch (error) {
      console.error('[AISkillsAssessmentService] submitResponse error:', error);
      throw error;
    }
  }

  /**
   * Finalize assessment and calculate results
   * @param {string} sessionId - Assessment session ID
   * @param {Array} conversationLog - Full conversation history (optional, fetched if not provided)
   * @returns {Promise<Object>} Final assessment results
   */
  async finalizeAssessment(sessionId, conversationLog = null) {
    try {
      // 1. Fetch session if conversation log not provided
      if (!conversationLog) {
        const { data: session, error: fetchError } = await supabase
          .from('ai_skill_assessment_sessions')
          .select('*')
          .eq('id', sessionId)
          .single();

        if (fetchError) {
          throw new SupabaseError(fetchError.message, fetchError, { table: 'ai_skill_assessment_sessions', action: 'fetch' });
        }

        conversationLog = session.conversation_log || [];
      }

      if (conversationLog.length === 0) {
        throw new Error('No conversation history found to finalize');
      }

      // 2. Calculate final score (average of all round scores)
      const totalScore = conversationLog.reduce((sum, entry) => {
        return sum + (entry.ai_evaluation?.score || 0);
      }, 0);
      const finalScore = Math.round((totalScore / conversationLog.length) * 100) / 100;

      // 3. Determine proficiency level
      let proficiencyLevel = 'beginner';
      if (finalScore >= 80) proficiencyLevel = 'expert';
      else if (finalScore >= 60) proficiencyLevel = 'advanced';
      else if (finalScore >= 31) proficiencyLevel = 'intermediate';

      // 4. Determine pass/fail
      const passed = finalScore >= PASS_THRESHOLD;

      // 5. Generate comprehensive feedback using AI
      const { data: session } = await supabase
        .from('ai_skill_assessment_sessions')
        .select('skill_name')
        .eq('id', sessionId)
        .single();

      const feedbackData = await generateAssessmentFeedback(
        conversationLog,
        finalScore,
        session?.skill_name || 'Unknown Skill'
      );

      // 6. Update session with final results
      const { error: updateError } = await supabase
        .from('ai_skill_assessment_sessions')
        .update({
          conversation_log: conversationLog,
          ai_evaluation: feedbackData,
          final_score: finalScore,
          proficiency_level: proficiencyLevel,
          passed: passed,
          completed_at: new Date().toISOString(),
          rounds_completed: conversationLog.length
        })
        .eq('id', sessionId);

      if (updateError) {
        throw new SupabaseError(updateError.message, updateError, { table: 'ai_skill_assessment_sessions', action: 'update' });
      }

      // 7. If passed, add verified skill to user profile
      if (passed && session) {
        const { data: sessionData } = await supabase
          .from('ai_skill_assessment_sessions')
          .select('user_id, skill_name')
          .eq('id', sessionId)
          .single();

        if (sessionData) {
          await this.addVerifiedSkillToProfile(
            sessionData.user_id,
            sessionData.skill_name,
            {
              sessionId,
              score: finalScore,
              proficiencyLevel,
              date: new Date().toISOString()
            }
          );
        }
      }

      return {
        passed,
        score: finalScore,
        proficiencyLevel,
        strengths: feedbackData.strengths || [],
        improvementAreas: feedbackData.improvement_areas || [],
        feedback: feedbackData.feedback || '',
        learningResources: feedbackData.learning_resources || []
      };
    } catch (error) {
      console.error('[AISkillsAssessmentService] finalizeAssessment error:', error);
      throw error;
    }
  }

  /**
   * Add verified skill to user profile
   * @param {string} userId - User ID
   * @param {string} skillName - Skill name
   * @param {Object} evaluationData - Assessment evaluation data
   */
  async addVerifiedSkillToProfile(userId, skillName, evaluationData) {
    try {
      // 1. Fetch current user profile skills
      const { data: profile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('skills')
        .eq('id', userId)
        .single();

      if (fetchError) {
        throw new SupabaseError(fetchError.message, fetchError, { table: 'user_profiles', action: 'fetch' });
      }

      let skills = profile?.skills || [];
      
      // 2. Find or create skill entry
      const skillIndex = skills.findIndex(s => 
        s.name?.toLowerCase() === skillName.toLowerCase()
      );

      const aiAssessmentData = {
        ai_assessed: true,
        ai_assessment_session_id: evaluationData.sessionId,
        ai_assessment_score: evaluationData.score,
        ai_proficiency_level: evaluationData.proficiencyLevel,
        ai_assessment_date: evaluationData.date,
        ai_model_used: import.meta.env.VITE_AI_MODEL || 'gemini-2.5-flash'
      };

      if (skillIndex >= 0) {
        // Update existing skill
        skills[skillIndex] = {
          ...skills[skillIndex],
          ...aiAssessmentData,
          verified: true // Mark as verified
        };
      } else {
        // Add new skill with AI verification
        skills.push({
          id: crypto?.randomUUID?.() || `skill-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,          name: skillName,
          type: 'upskills',
          verified: true,
          ...aiAssessmentData
        });
      }

      // 3. Update user profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ skills })
        .eq('id', userId);

      if (updateError) {
        throw new SupabaseError(updateError.message, updateError, { table: 'user_profiles', action: 'update' });
      }

      console.log(`[AISkillsAssessmentService] Added verified skill ${skillName} to profile`);
    } catch (error) {
      console.error('[AISkillsAssessmentService] addVerifiedSkillToProfile error:', error);
      // Don't throw - this is a nice-to-have, not critical
    }
  }

  /**
   * Get skill ID by name (case-insensitive)
   * @param {string} skillName - Skill name
   * @returns {Promise<number|null>} Skill ID or null
   */
  async getSkillIdByName(skillName) {
    try {
      const { data, error } = await supabase
        .from('skills')
        .select('id')
        .ilike('name', skillName)
        .limit(1);

      if (error) {
        console.error('[AISkillsAssessmentService] getSkillIdByName query error:', error);
        return null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      return data[0].id;
    } catch (error) {
      console.error('[AISkillsAssessmentService] getSkillIdByName error:', error);
      return null;
    }
  }

  /**
   * Check if user can retake assessment (cooldown check)
   * @param {string} userId - User ID
   * @param {string} skillName - Skill name
   * @returns {Promise<Object>} { allowed: boolean, message: string }
   */
  async checkRetakeCooldown(userId, skillName) {
    try {
      // Fetch most recent completed assessment (passed OR failed)
      const { data: lastAttempt, error: attemptError } = await supabase
        .from('ai_skill_assessment_sessions')
        .select('completed_at, passed')
        .eq('user_id', userId)
        .eq('skill_name', skillName)
        .not('completed_at', 'is', null)  // Must be completed
        .order('completed_at', { ascending: false })
        .limit(1);

      if (attemptError) {
        console.error('[AISkillsAssessmentService] Error fetching last assessment:', attemptError);
        // Default to allowing if check fails
        return { allowed: true };
      }

      // If no completed assessments, allow new one
      if (!lastAttempt || lastAttempt.length === 0) {
        return { allowed: true };
      }

      const completedAt = lastAttempt[0].completed_at;
      const passed = lastAttempt[0].passed;

      // If passed, can always retake after cooldown
      // If failed, can retake after cooldown
      const lastAttemptTime = new Date(completedAt);
      const now = new Date();
      const timeElapsedMs = now - lastAttemptTime;
      const hoursElapsed = timeElapsedMs / (1000 * 60 * 60);
      const hoursRemaining = Math.max(0, Math.ceil(RETRY_COOLDOWN_HOURS - hoursElapsed));

      if (hoursRemaining > 0) {
        return {
          allowed: false,
          message: `You can retake this assessment in ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''}`
        };
      }

      return { allowed: true };
    } catch (error) {
      console.error('[AISkillsAssessmentService] checkRetakeCooldown error:', error);
      // Default to allowing if check fails
      return { allowed: true };
    }
  }

  /**
   * Get assessment history for a user
   * @param {string} userId - User ID
   * @param {string} skillName - Optional: filter by skill name
   * @returns {Promise<Array>} Assessment history
   */
  async getAssessmentHistory(userId, skillName = null) {
    try {
      let query = supabase
        .from('ai_skill_assessment_sessions')
        .select('*')
        .eq('user_id', userId)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });

      if (skillName) {
        query = query.eq('skill_name', skillName);
      }

      const { data, error } = await query;

      if (error) {
        throw new SupabaseError(error.message, error, { table: 'ai_skill_assessment_sessions', action: 'fetch' });
      }

      return data || [];
    } catch (error) {
      console.error('[AISkillsAssessmentService] getAssessmentHistory error:', error);
      return [];
    }
  }

  /**
   * Get session details by ID
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Session details
   */
  async getSessionDetails(sessionId) {
    try {
      const { data, error } = await supabase
        .from('ai_skill_assessment_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) {
        throw new SupabaseError(error.message, error, { table: 'ai_skill_assessment_sessions', action: 'fetch' });
      }

      return data;
    } catch (error) {
      console.error('[AISkillsAssessmentService] getSessionDetails error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new AISkillsAssessmentService();
